'use client';

import { useEffect, useState } from 'react';
import { useSearchParams } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { CheckCircle2, Loader2, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';

type VerifyState = 'checking' | 'success' | 'error';

export function VerifyEmailPage() {
  const t = useTranslations('auth.verifyEmail');
  const searchParams = useSearchParams();
  const [state, setState] = useState<VerifyState>('checking');
  const callbackURL = searchParams.get('callbackURL') || '/my/dashboard';

  useEffect(() => {
    let cancelled = false;

    async function verify() {
      const token = searchParams.get('token');
      if (!token) {
        if (!cancelled) setState('error');
        return;
      }

      try {
        const res = await fetch(
          `/api/auth/verify-email?token=${encodeURIComponent(token)}&callbackURL=${encodeURIComponent(callbackURL)}`,
          { credentials: 'include' },
        );
        if (!cancelled) setState(res.ok ? 'success' : 'error');
      } catch {
        if (!cancelled) setState('error');
      }
    }

    verify();
    return () => {
      cancelled = true;
    };
  }, [searchParams]);

  return (
    <div
      className="notranslate flex min-h-[80vh] items-center justify-center bg-stone-950 p-6"
      translate="no"
    >
      <div className="max-w-md w-full rounded-xl border border-stone-800 bg-stone-900 p-8 text-center">
        <h1 className="text-xl font-semibold text-white mb-6">{t('title')}</h1>

        {state === 'checking' && (
          <div className="flex flex-col items-center gap-3">
            <Loader2 className="w-8 h-8 text-stone-400 animate-spin" />
            <p className="text-stone-400">{t('checking')}</p>
          </div>
        )}

        {state === 'success' && (
          <div className="flex flex-col items-center gap-3">
            <CheckCircle2 className="w-10 h-10 text-green-500" />
            <p className="text-white font-medium">{t('success')}</p>
            <p className="text-stone-400 text-sm">{t('successDescription')}</p>
            <Button asChild className="mt-4">
              <a href={callbackURL}>{t('continue')}</a>
            </Button>
          </div>
        )}

        {state === 'error' && (
          <div className="flex flex-col items-center gap-3">
            <XCircle className="w-10 h-10 text-red-500" />
            <p className="text-white font-medium">{t('error')}</p>
            <p className="text-stone-400 text-sm">{t('errorDescription')}</p>
          </div>
        )}
      </div>
    </div>
  );
}
